import { Button, Column, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { CenteredSpinner } from '~/commonComponents';

/**
 * Full-render loading state shared by the BID screens/tabs. Wraps the common
 * `CenteredSpinner` in a flex column so the indicator sits centered with a
 * comfortable vertical footprint, and forwards `data-cy` to the container so
 * callers keep their existing test hooks.
 */
export const BidLoadingState = ({ 'data-cy': dataCy }: { 'data-cy'?: string }) => (
  <Column flex width="100%" data-cy={dataCy}>
    <CenteredSpinner minHeight={128} />
  </Column>
);

/**
 * Full-render error-with-retry state shared by the BID query screens (history
 * page, application form). Renders the message and a "Try again" button wired
 * to the caller's `refetch`.
 */
export const BidErrorState = ({
  message,
  onRetry,
  'data-cy': dataCy,
}: {
  message: string;
  onRetry: () => void;
  'data-cy'?: string;
}) => (
  <Column align="center" gap={4} py={16} data-cy={dataCy}>
    <Typography>{message}</Typography>
    <Button onClick={onRetry}>Try again</Button>
  </Column>
);

/**
 * Inline submit-error-with-retry state shared by the BID mutation forms
 * (application, EOD/pending feedback). Renders the error message and an
 * outlined "Try again" button wired to the caller's `mutation.reset()`.
 * Callers gate it on `mutation.isError`.
 */
export const BidSubmitError = ({
  message,
  onRetry,
  'data-cy': dataCy,
}: {
  message: string;
  onRetry: () => void;
  'data-cy'?: string;
}) => (
  <Column gap={2} data-cy={dataCy}>
    <Typography color={colors.error[500]}>{message}</Typography>
    <Button type="button" variant="outlined" onClick={onRetry}>
      Try again
    </Button>
  </Column>
);
